// React, Next
import { ReactElement, useEffect, useRef, useState, RefObject } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

// MUI
import Box from '@mui/material/Box'
import { motion } from 'framer-motion'

// Types
import { SectionKey } from '@/types/global_types'

// Sections
import GetStartedSection from '@/components/getStarted/GetStartedSection'
import OurServicesSection from './sections/OurServicesSection'
import ServiceDetailsSection from './sections/ServiceDetailsSection'
import WhyWorkWithUsSection from './sections/WhyWorkWithUsSection'

// Styles
import classes from './ServicesView-CSS'

export default function ServicesView() {
  const { t } = useTranslation('services')
  const router = useRouter()
  const sectionParam = router.query.section as SectionKey | undefined

  const sectionRefs: Record<SectionKey, RefObject<HTMLDivElement | null>> = {
    ourServices: useRef(null),
    serviceDetails: useRef(null),
    whyWorkWithUs: useRef(null),
    getStarted: useRef(null)
  }

  const initialVisibility: Record<SectionKey, boolean> = {
    ourServices: false,
    serviceDetails: false,
    whyWorkWithUs: false,
    getStarted: false
  }

  const [visibility, setVisibility] = useState(initialVisibility)

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const id = entry.target.getAttribute('data-section') as SectionKey
          if (!id) return
          setVisibility(prev => ({ ...prev, [id]: entry.isIntersecting }))
        })
      },
      { threshold: 0.1 }
    )

    Object.keys(sectionRefs).forEach(key => {
      const ref = sectionRefs[key as SectionKey].current
      if (ref) observer.observe(ref)
    })

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (sectionParam && sectionRefs[sectionParam]?.current) {
      scrollToSection(sectionParam)
    }
  }, [sectionParam])

  const scrollToSection = (section: SectionKey) => {
    sectionRefs[section]?.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const getSection = ({
    id,
    section,
    reference,
    visible
  }: {
    id: SectionKey
    section: ReactElement
    reference: RefObject<HTMLDivElement | null>
    visible: boolean
  }) => (
    <motion.div
      ref={reference}
      data-section={id}
      initial={{ opacity: 0, y: 20 }}
      animate={visible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ delay: 0.07, duration: 0.75 }}
    >
      {section}
    </motion.div>
  )

  return (
    <Box style={classes.main}>
      {getSection({
        id: 'ourServices',
        section: <OurServicesSection />,
        reference: sectionRefs.ourServices,
        visible: visibility.ourServices
      })}

      {getSection({
        id: 'serviceDetails',
        section: <ServiceDetailsSection />,
        reference: sectionRefs.serviceDetails,
        visible: visibility.serviceDetails
      })}

      {getSection({
        id: 'whyWorkWithUs',
        section: <WhyWorkWithUsSection />,
        reference: sectionRefs.whyWorkWithUs,
        visible: visibility.whyWorkWithUs
      })}

      {getSection({
        id: 'getStarted',
        section: (
          <GetStartedSection
            title={t('started.title')}
            subTitle={t('started.subTitle')}
            title2={t('started.title2')}
            subTitle2={t('started.subTitle2')}
            button={t('started.button')}
          />
        ),
        reference: sectionRefs.getStarted,
        visible: visibility.getStarted
      })}
    </Box>
  )
}
